import { useState, useEffect, useRef } from 'react';
import { GraduationCap } from 'lucide-react';

/**
 * Splash screen mobile do Aluno Conectado.
 * Exibe por 2.5s na primeira renderização, depois desaparece com fade-out.
 */
export function MobileSplash({ onFinish }: { onFinish: () => void }) {
  const [phase, setPhase] = useState<'enter' | 'visible' | 'exit'>('enter');
  const onFinishRef = useRef(onFinish);
  onFinishRef.current = onFinish;

  useEffect(() => {
    const t1 = setTimeout(() => setPhase('visible'), 100);
    const t2 = setTimeout(() => setPhase('exit'), 2200);
    const t3 = setTimeout(() => onFinishRef.current(), 2700);
    return () => { clearTimeout(t1); clearTimeout(t2); clearTimeout(t3); };
  }, []);

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 9999,
      background: 'linear-gradient(165deg, #0f2744 0%, #1e3a5f 30%, #2563eb 70%, #3b82f6 100%)',
      display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
      color: 'white',
      opacity: phase === 'enter' ? 0 : phase === 'exit' ? 0 : 1,
      transition: 'opacity 0.5s ease',
    }}>
      {/* Decorative circles */}
      <div style={{ position: 'absolute', top: -60, right: -60, width: 200, height: 200, borderRadius: '50%', background: 'rgba(59,130,246,0.12)', filter: 'blur(40px)' }} />
      <div style={{ position: 'absolute', bottom: -40, left: -40, width: 160, height: 160, borderRadius: '50%', background: 'rgba(96,165,250,0.08)', filter: 'blur(30px)' }} />
      <div style={{ position: 'absolute', top: '30%', left: '15%', width: 80, height: 80, borderRadius: '50%', background: 'rgba(255,255,255,0.03)', filter: 'blur(20px)' }} />

      {/* Logo icon */}
      <div style={{
        width: 88, height: 88, borderRadius: 24,
        background: 'rgba(255,255,255,0.12)',
        border: '2px solid rgba(255,255,255,0.15)',
        backdropFilter: 'blur(12px)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        marginBottom: 24,
        transform: phase === 'visible' ? 'scale(1) translateY(0)' : 'scale(0.8) translateY(10px)',
        transition: 'transform 0.6s cubic-bezier(0.34,1.56,0.64,1)',
        boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
      }}>
        <GraduationCap size={44} strokeWidth={1.5} style={{ color: 'white', filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.2))' }} />
      </div>

      {/* Brand name */}
      <div style={{
        fontSize: '1.6rem', fontWeight: 800, letterSpacing: '-0.5px',
        marginBottom: 6,
        transform: phase === 'visible' ? 'translateY(0)' : 'translateY(8px)',
        opacity: phase === 'visible' ? 1 : 0,
        transition: 'all 0.5s ease 0.15s',
      }}>
        Aluno Conectado
      </div>

      {/* Subtitle */}
      <div style={{
        fontSize: '0.78rem', fontWeight: 500, color: 'rgba(255,255,255,0.55)',
        letterSpacing: '0.5px',
        transform: phase === 'visible' ? 'translateY(0)' : 'translateY(6px)',
        opacity: phase === 'visible' ? 1 : 0,
        transition: 'all 0.5s ease 0.25s',
      }}>
        Painel do Professor
      </div>

      {/* Loading dots */}
      <div style={{
        display: 'flex', gap: 6, marginTop: 40,
        opacity: phase === 'visible' ? 1 : 0,
        transition: 'opacity 0.4s ease 0.4s',
      }}>
        {[0, 1, 2].map(i => (
          <span key={i} style={{
            width: 6, height: 6, borderRadius: '50%',
            background: 'rgba(255,255,255,0.5)',
            animation: `splashDot 1.2s ease-in-out ${i * 0.2}s infinite`,
          }} />
        ))}
      </div>

      {/* Bottom branding */}
      <div style={{
        position: 'absolute', bottom: 40, left: 0, right: 0, textAlign: 'center',
        fontSize: '0.62rem', color: 'rgba(255,255,255,0.25)', fontWeight: 600,
        letterSpacing: '1.5px', textTransform: 'uppercase',
      }}>
        Escola Segura e Inteligente
      </div>

      <style>{`
        @keyframes splashDot {
          0%, 80%, 100% { transform: scale(0.6); opacity: 0.4; }
          40% { transform: scale(1.2); opacity: 1; }
        }
      `}</style>
    </div>
  );
}
